import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import dayjs from 'dayjs';
import {OverlaySpinnerComponent} from '@shared/components/overlay-spinner/overlay-spinner.component';
import {PatientStatisticsService} from '@shared/api/services/v2/patient-statistics.service';
import {
    isWeeklySnapshotV2,
    PatientWeeklyAdherenceV2,
    PatientWeeklySnapshotV2,
    WeeklyDailyCheckinsRollupV2,
    WeeklyEd15SurveysV2,
    WeeklySnapshot_Chapter
} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {DailyBehaviourV2} from '@shared/api/models/query/patient-daily-snapshot.model';
import {WeeklyAdherenceDonutsComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-adherence-donuts.component';
import {WeeklySurveyCardComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-survey-card.component';
import {WeeklyChapterListComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-chapter-list.component';
import {WeeklyDailyCheckinsRollupComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-daily-checkins-rollup/weekly-daily-checkins-rollup.component';
import {DailyBehaviourPanelComponent} from '@cdss/pages/patient-overview-v2/detail/daily/components/daily-behaviour-panel.component';
import {PatientDetailHeaderComponent} from '@cdss/pages/patient-overview-v2/components/patient-detail-header/patient-detail-header.component';
import {
    DailyPatientCarerToggleComponent,
    DailyPatientCarerToggleValue
} from '@cdss/pages/patient-overview-v2/detail/daily/components/daily-patient-carer-toggle.component';
import {Patient} from '@shared/api/models/query/patient.model';
import {
    emptyWeeklyAdherence,
    emptyWeeklyBehaviours,
    isEmptyWeeklySnapshot
} from '@cdss/pages/patient-overview-v2/detail/detail-no-data-placeholder';

@Component({
    selector: 'cdss-patient-weekly-detail-page',
    standalone: true,
    imports: [
        CommonModule,
        OverlaySpinnerComponent,
        WeeklyAdherenceDonutsComponent,
        WeeklySurveyCardComponent,
        WeeklyChapterListComponent,
        WeeklyDailyCheckinsRollupComponent,
        DailyBehaviourPanelComponent,
        PatientDetailHeaderComponent,
        DailyPatientCarerToggleComponent,
    ],
    templateUrl: './patient-weekly-detail.page.html',
    styleUrl: './patient-weekly-detail.page.scss'
})
export class PatientWeeklyDetailPage implements OnInit, OnDestroy {

    loading = true;
    patientId!: number;
    dateStart!: string;
    weekNumber: number | null = null;
    snapshot: PatientWeeklySnapshotV2 | null = null;
    noData = false;
    patient: Patient | null = null;

    private querySub: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private patientStatisticsService: PatientStatisticsService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.patientId = Number(this.route.snapshot.params['id']);

        this.querySub = this.route.queryParams.subscribe(async (params) => {
            const dateStart = params['dateStart'] ?? null;
            if (!dateStart) {
                this.toastr.error('Missing dateStart parameter');
                this.loading = false;
                return;
            }
            this.dateStart = dateStart;
            this.weekNumber = params['weekNumber'] ? Number(params['weekNumber']) : null;
            this.loading = true;
            this.snapshot = null;
            this.noData = false;
            await this.loadSnapshot();
        });
    }

    ngOnDestroy(): void {
        this.querySub?.unsubscribe();
    }

    private async loadSnapshot(): Promise<void> {
        try {
            const response = await this.patientStatisticsService.getWeeklyDetail(
                this.patientId,
                this.dateStart,
                'patient'
            );
            // UNIVAPLAT-3042: any payload that isn't the v2 shape — the explicit
            // WeeklyNoSnapshot marker, or a pre-regeneration row with no payloadVersion
            // — is treated identically as "no v2 data". The legacy shape is no longer
            // typed or parsed. UNIVAPLAT-3259: a v2 snapshot with nothing reported or
            // completed is also "no data", so it renders exactly like a missing one.
            const hasData = isWeeklySnapshotV2(response) && !isEmptyWeeklySnapshot(response);
            this.noData = !hasData;
            this.snapshot = hasData ? response : null;
        } catch (error: any) {
            this.toastr.error(error?.error?.message ?? 'Failed to load weekly snapshot');
        } finally {
            this.loading = false;
        }
    }

    /**
     * ED-15 surveys panel data (UNIVAPLAT-3035). `snapshot` is only ever the v2 shape
     * (UNIVAPLAT-3042) — a pre-regeneration/no-data week never reaches this getter's
     * consumer since `snapshot` itself is `null`, so the survey card's own "No
     * check-in" empty state handles it.
     */
    get ed15Surveys(): WeeklyEd15SurveysV2 | null {
        return this.snapshot?.surveys ?? null;
    }

    /** Daily check-ins roll-up panel data (UNIVAPLAT-3036), v2-only — see `ed15Surveys` above. */
    get dailyCheckinsRollup(): WeeklyDailyCheckinsRollupV2 | null {
        return this.snapshot?.dailyCheckins ?? null;
    }

    /**
     * Behaviours panel data (UNIVAPLAT-3036 follow-up), mapped onto the exact same
     * `DailyBehaviourV2[]` shape and `cdss-daily-behaviour-panel` component the Daily
     * Summary page uses, so the two pages render identically. The weekly payload's
     * `observedBehaviours` carries both the patient's and the carer's side per
     * behaviour (`{name, patient: {...}, carer: {...}}`) — this panel is the patient's
     * own "Behaviour log", so only `.patient` is used; the carer comparison data is not
     * shown here. `percentage` is the backend's real weekly-cap-relative value
     * (UNIVAPLAT-3037 fix-up — previously approximated client-side from `severity`'s
     * 4-value bucket, which pegged unrelated counters to the same 0/30/65/100 widths).
     */
    get patientBehaviourLog(): DailyBehaviourV2[] | null {
        const list = this.snapshot?.observedBehaviours;
        if (!list) return this.noData ? emptyWeeklyBehaviours('patient') : null;
        return list.map(behaviour => ({
            name: behaviour.name,
            counter: behaviour.patient.value,
            severity: behaviour.patient.severity,
            trend: behaviour.patient.trend,
            percentage: behaviour.patient.percentage,
        }));
    }

    get weekEndDate(): string {
        return dayjs(this.dateStart).add(6, 'day').format('YYYY-MM-DD');
    }

    get weekLabel(): string {
        if (this.weekNumber !== null) {
            return `Week ${this.weekNumber}`;
        }
        return 'Weekly';
    }

    get weekRangeLabel(): string {
        if (!this.dateStart) return '';
        const start = dayjs(this.dateStart);
        const end = start.add(6, 'day');
        const sameMonth = start.month() === end.month();
        if (sameMonth) {
            return `${start.format('D')}–${end.format('D MMMM')}`;
        }
        return `${start.format('D MMM')} – ${end.format('D MMM')}`;
    }

    get isCurrentWeek(): boolean {
        const startOfCurrentWeek = dayjs().startOf('week');
        return dayjs(this.dateStart).isSame(startOfCurrentWeek, 'day') ||
            dayjs(this.dateStart).isAfter(startOfCurrentWeek, 'day');
    }

    onPatientLoaded(patient: Patient): void {
        this.patient = patient;
    }

    /** Only shown when both a patient-side record and a carer record exist — otherwise
     *  there is nothing to switch to. Mirrors the Daily Summary page's toggle-visibility rule. */
    get showPatientCarerToggle(): boolean {
        return !!this.patient?.user?.phoneNumber && !!this.patient?.carer;
    }

    onPatientCarerToggleSelect(value: DailyPatientCarerToggleValue): void {
        if (value === 'carer') {
            this.router.navigate(
                ['/cdss/patient-overview', this.patientId, 'carer-weekly'],
                {queryParams: {dateStart: this.dateStart, ...(this.weekNumber !== null ? {weekNumber: this.weekNumber} : {})}}
            );
        }
    }

    /** Adherence for the donuts; zeroed when the week has no data (UNIVAPLAT-3259). */
    get adherence(): PatientWeeklyAdherenceV2 {
        return this.snapshot?.adherence ?? emptyWeeklyAdherence('patient');
    }

    /** Chapters for the list; empty (its own "No chapters" state) when the week has no data. */
    get chapters(): WeeklySnapshot_Chapter[] {
        return this.snapshot?.chapters ?? [];
    }

    get chapterAdherencePct(): number {
        const a = this.snapshot?.adherence?.chapters;
        if (!a || a.scheduled === 0) return 0;
        return Math.round(a.completed * 100 / a.scheduled);
    }

    get checkInAdherencePct(): number {
        const a = this.snapshot?.adherence?.checkIns;
        if (!a || a.scheduled === 0) return 0;
        return Math.round(a.completed * 100 / a.scheduled);
    }

    get foodDiaryPct(): number {
        // v2's foodDiary is {scheduled, completed} (UNIVAPLAT-3033 OQ-D, renamed from
        // the retired per-meal-total legacy field pair — UNIVAPLAT-3042 drops the
        // dual-shape read now that `snapshot` is v2-only).
        const foodDiary = this.snapshot?.adherence?.foodDiary;
        if (!foodDiary || foodDiary.scheduled === 0) return 0;
        return Math.round(foodDiary.completed * 100 / foodDiary.scheduled);
    }

    navigateToPrevWeek(): void {
        const prevWeekStart = dayjs(this.dateStart).subtract(7, 'day').format('YYYY-MM-DD');
        const weekNumber = this.weekNumber !== null ? this.weekNumber - 1 : null;
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId, 'patient-weekly'],
            {queryParams: {dateStart: prevWeekStart, ...(weekNumber !== null ? {weekNumber} : {})}}
        );
    }

    navigateToNextWeek(): void {
        if (this.isCurrentWeek) return;
        const nextWeekStart = dayjs(this.dateStart).add(7, 'day').format('YYYY-MM-DD');
        const weekNumber = this.weekNumber !== null ? this.weekNumber + 1 : null;
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId, 'patient-weekly'],
            {queryParams: {dateStart: nextWeekStart, ...(weekNumber !== null ? {weekNumber} : {})}}
        );
    }

    /** Mirrors the Daily Summary page's Back behaviour (UNIVAPLAT-2360 follow-up): return to the
     *  Overview page on the correct patient/carer tab, opened on the week currently displayed
     *  here rather than always falling back to the care programme's current week. */
    goBack(): void {
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId],
            {queryParams: {type: 'patient', dateStart: this.dateStart}}
        );
    }
}
